import React, { useEffect, useState } from 'react';
import { Car, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TravelSettings = ({ token }) => {
  const [rate, setRate] = useState('0.35');
  const [freeMiles, setFreeMiles] = useState('10');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/travel`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRate(String(data.rate_per_mile));
          setFreeMiles(String(data.free_miles_per_leg));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/travel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rate_per_mile: parseFloat(rate),
          free_miles_per_leg: parseFloat(freeMiles),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setRate(String(data.rate_per_mile));
        setFreeMiles(String(data.free_miles_per_leg));
        setMessage({ type: 'ok', text: 'Travel settings saved. All travel money updates automatically.' });
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Could not save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not save settings' });
    } finally {
      setSaving(false);
    }
  };

  const exampleMiles = 60;
  const example = Math.max(0, exampleMiles - (parseFloat(freeMiles) || 0)) * 2 * (parseFloat(rate) || 0);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <Card className="bg-white max-w-2xl" data-testid="travel-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Car className="w-5 h-5 text-blue-600" />
          Travel Money
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Rate per mile (£)</label>
            <Input
              type="number" step="0.01" min="0" value={rate}
              onChange={(e) => setRate(e.target.value)}
              data-testid="travel-rate-input"
            />
            <p className="text-xs text-slate-500 mt-1">Default 0.35 (35p)</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Free miles each way</label>
            <Input
              type="number" step="1" min="0" value={freeMiles}
              onChange={(e) => setFreeMiles(e.target.value)}
              data-testid="travel-free-miles-input"
            />
            <p className="text-xs text-slate-500 mt-1">Default 10 miles per leg, unpaid</p>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
          <p className="font-medium text-slate-800 mb-1">How it works</p>
          <p>
            Driving distance from the staff member's home to the event, both ways.
            A {exampleMiles} mile job pays ({exampleMiles} − {freeMiles || 0}) × 2 ={' '}
            {Math.max(0, exampleMiles - (parseFloat(freeMiles) || 0)) * 2} paid miles ={' '}
            <span className="font-semibold text-slate-900">£{example.toFixed(2)}</span>.
          </p>
        </div>

        {message && (
          <p className={`text-sm ${message.type === 'ok' ? 'text-green-700' : 'text-red-600'}`} data-testid="travel-settings-message">
            {message.text}
          </p>
        )}

        <Button onClick={save} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600" data-testid="travel-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save travel settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default TravelSettings;
